import { Button } from "@/components/ui/button";
import formatTemplateString from "@/utils/formatTemplateString";

interface ReferralCreditUIPropsV1 {
  /** Total claimable points (shown when not yet used). */
  claimablePoints: number;
  /** The credit amount being applied (capped to upgrade price). */
  usableCredit: number;
  /** Whether the user has opted to use their credits. */
  isUsed: boolean;
  /** Toggles use/remove. */
  onToggle: () => void;
  /** Disables the use/remove button. */
  disabled?: boolean;
  /** Template string for the "use" prompt. `${0}` is replaced with the amount. */
  text?: string;
}

/**
 * Rainbow-gradient banner that lets the user apply or remove
 * their referral credits toward the current purchase.
 *
 * Pure UI — all data fetching and state live in `useReferralCreditComponent`.
 */
export function ReferralCreditUIV1({
  claimablePoints,
  usableCredit,
  isUsed,
  onToggle,
  disabled = false,
  text = "Use your Referral Credits worth ${0}",
}: ReferralCreditUIPropsV1) {
  const displayText = isUsed
    ? `Using Referral Credits worth $${usableCredit.toFixed(2)}`
    : formatTemplateString(text, claimablePoints.toFixed(2));

  return (
    <div className="rainbow-gradient-bg flex h-10 w-full shrink-0 items-center rounded-lg p-px">
      <div className="flex h-full w-full items-center rounded-[7px] bg-white">
        <div className="inline-flex h-full w-full items-center justify-between rounded-[6px] bg-[linear-gradient(270deg,rgba(126,104,176,0.05)_0%,rgba(0,138,210,0.05)_18%,rgba(33,178,75,0.05)_35%,rgba(255,196,14,0.05)_52%,rgba(246,130,31,0.05)_69%,rgba(239,64,35,0.05)_85%,rgba(126,104,176,0.05)_100%)] px-2">
          <div className="flex flex-col gap-0.5">
            <p className="font-archivo text-xs leading-[21.33px] font-medium text-[#0D0D0D] sm:text-sm">
              {displayText}
            </p>

            {/* {isUsed && remainingBalance > 0 && (
              <p className="font-archivo text-xs text-gray-500">
                Remaining balance: ${remainingBalance.toFixed(2)}
              </p>
            )} */}
          </div>

          <button
            onClick={onToggle}
            disabled={disabled}
            className="flex items-center justify-center rounded-md font-archivo text-[14px] leading-5 font-medium text-[#6599FF] backdrop-blur-[9px] transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUsed ? "Remove" : "Use"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ReferralCreditUIProps {
  /** Total claimable points (shown when not yet used). */
  totalPoints: number;
  /** The credit amount being applied (capped to upgrade price). */
  usableCredit: number;
  /** Whether the user has opted to use their credits. */
  isUsed: boolean;
  /** Toggles use/remove. */
  onToggle: () => void;
  /** Disables the use/remove button. */
  disabled?: boolean;
  /** Template string for the "use" prompt. `${0}` is replaced with the amount. */
  text?: string;
}

export function ReferralCreditUI({
  totalPoints,
  usableCredit,
  isUsed,
  onToggle,
  disabled = false,
  text = "Referral Credits",
}: ReferralCreditUIProps) {
  //
  const displayText = isUsed ? text : `${text} ($${totalPoints.toFixed(2)})`;

  return (
    <div className="flex h-full w-full items-center rounded-[7px]">
      <div className="inline-flex h-full w-full items-center justify-between rounded-[6px] text-base">
        <div className="flex gap-1">
          <p className="text-base leading-[22.4px] font-medium tracking-[0.16px] text-type-1">
            {displayText}
          </p>

          <Button
            variant="inline-accent"
            onClick={onToggle}
            disabled={disabled}
          >
            {isUsed ? "Remove" : "Use Now"}
          </Button>
        </div>

        {isUsed && (
          <p className="text-base leading-[22.4px] font-medium tracking-[0.16px] text-[#3E8174]">
            -${usableCredit.toFixed(2)}
          </p>
        )}
      </div>
    </div>
  );
}
